import type { DriveStep } from "driver.js";
import { ONBOARDING_IDS } from "../constants";

export function getTournamentCompareSteps(isMobile: boolean): DriveStep[] {
    const steps: DriveStep[] = [
        {
            element: `#${ONBOARDING_IDS.TOURNAMENT_VIEW_MODE}`,
            popover: {
                title: "Average vs Total",
                description:
                    "Toggle whether the KPI breakdown shows each player's per-game average or their cumulative total across the tournament.",
                side: "bottom",
                align: "start",
            },
        },
        {
            element: `#${ONBOARDING_IDS.TOURNAMENT_COMPARE_EXPORT}`,
            popover: {
                title: "Export Comparison",
                description:
                    "Once two players are selected, download an Excel file with the full head-to-head breakdown including every KPI category.",
                side: "bottom",
                align: "end",
            },
        },
        {
            element: `#${ONBOARDING_IDS.COMPARE_PLAYER_SELECT}`,
            popover: {
                title: "Player Selection",
                description:
                    "Search and pick two players to compare — field players and goalkeepers are both available. " +
                    "The comparison table and radar chart below update instantly as you make your selection.",
                side: "bottom",
                align: "center",
            },
        },
        {
            element: isMobile
                ? `#${ONBOARDING_IDS.COMPARE_STATS_TABLE_MOBILE}`
                : `#${ONBOARDING_IDS.COMPARE_STATS_TABLE}`,
            onHighlightStarted: (el) => el?.scrollIntoView({ behavior: "instant", block: "center" }),
            popover: {
                title: "KPI Breakdown Table",
                description:
                    "A side-by-side breakdown of every KPI category: Defensive, Offensive, Team Play, Effort, and Bonus points. " +
                    "The Total KPI row shows the colour-coded tier badge for each player.",
                side: "right",
                align: "start",
            },
        },
    ];

    if (!isMobile) {
        steps.push({
            element: `#${ONBOARDING_IDS.COMPARE_RADAR}`,
            popover: {
                title: "Radar Chart",
                description:
                    "A visual overlay of both players across all KPI categories — the larger the filled area, the stronger the overall performance. " +
                    "Great for spotting complementary strengths at a glance.",
                side: "left",
                align: "start",
            },
        });
    }

    steps.push({
        element: `#${ONBOARDING_IDS.COMPARE_PERFORMANCE}`,
        onHighlightStarted: (el) => el?.scrollIntoView({ behavior: "instant", block: "center" }),
        popover: {
            title: "Match Performance Comparison",
            description:
                "Tracks how each player's chosen KPI metric evolved game by game across the tournament, with both players plotted on the same chart. " +
                "Use the tabs to switch between Total Points, Offensive, Defensive, Team Play, Effort, or Bonus — goalkeepers show their own categories. " +
                "Enable trend lines to see the trajectory, or filter to common matches only to compare games both players participated in.",
            side: "top",
            align: "center",
        },
    });

    return steps;
}
